import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { fetchRecords } from '../services/api';

export const generatePayslipPDF = async (payslipId) => {
  try {
    // 1. Fetch Payslip Record First
    const psRes = await fetchRecords('PAYSLIP');
    const allPayslips = psRes.data?.data || [];
    const payslip = allPayslips.find(p => p.payslip_id === payslipId || p.id === payslipId);
    
    if (!payslip) throw new Error('Payslip not found.');
    if (['DRAFT', 'CANCELLED'].includes(payslip.status)) {
      throw new Error(`Cannot generate PDF for payslip in ${payslip.status} status.`);
    }

    // 2. Parallel Data Fetching
    const [
      runReq,
      periodReq,
      asnReq,
      perReq,
      attReq,
      ppmReq,
      elReq,
      eeReq,
      bgReq,
      payReq,
      companyReq,
      bankReq,
      deptReq,
      desigReq
    ] = await Promise.all([
      fetchRecords('PAYROLL_RUN').catch(() => ({ data: { data: [] } })),
      fetchRecords('PAY_PERIOD').catch(() => ({ data: { data: [] } })),
      fetchRecords('ASSIGNMENT').catch(() => ({ data: { data: [] } })),
      fetchRecords('PERSON').catch(() => ({ data: { data: [] } })),
      fetchRecords('ATTENDANCE').catch(() => ({ data: { data: [] } })),
      fetchRecords('PERSONAL_PAYMENT_METHOD').catch(() => ({ data: { data: [] } })),
      fetchRecords('ELEMENT_TYPE').catch(() => ({ data: { data: [] } })),
      fetchRecords('ELEMENT_ENTRY').catch(() => ({ data: { data: [] } })),
      fetchRecords('BUSINESS_GROUP').catch(() => ({ data: { data: [] } })),
      fetchRecords('PAYMENT').catch(() => ({ data: { data: [] } })),
      fetchRecords('COMPANY').catch(() => ({ data: { data: [] } })),
      fetchRecords('BANK_MASTER').catch(() => ({ data: { data: [] } })),
      fetchRecords('DEPARTMENT').catch(() => ({ data: { data: [] } })),
      fetchRecords('DESIGNATION').catch(() => ({ data: { data: [] } }))
    ]);

    // 3. Hydrate Data
    const companyList = companyReq.data?.data || [];
    const bankList = bankReq.data?.data || [];
    const deptList = deptReq.data?.data || [];
    const desigList = desigReq.data?.data || [];

    const run = (runReq.data?.data || []).find(r => r.run_id === payslip.run_id);
    const period = run ? (periodReq.data?.data || []).find(p => p.period_id === run.period_id) : null;
    const assignment = (asnReq.data?.data || []).find(a => a.assignment_id === payslip.assignment_id);
    const person = assignment ? (perReq.data?.data || []).find(p => p.person_id === assignment.person_id) : null;
    const bg = assignment ? (bgReq.data?.data || []).find(b => b.bg_id === assignment.bg_id) : null;
    const company = assignment ? companyList.find(c => c.company_id === assignment.COMPANY_id) : null;
    const dept = assignment ? deptList.find(d => d.department_id === assignment.department_id) : null;
    const desig = assignment ? desigList.find(d => d.designation_id === assignment.designation_id) : null;
    const payment = (payReq.data?.data || []).find(p => p.run_id === payslip.run_id && p.assignment_id === payslip.assignment_id);

    // Filter Attendance
    let presentDays = 0, paidLeaves = 0, absentDays = 0, otHours = 0;
    if (period) {
      const start = new Date(period.start_date);
      const end = new Date(period.end_date || period.start_date);
      const atts = (attReq.data?.data || []).filter(a => {
        const ad = new Date(a.attendance_date);
        return a.assignment_id === payslip.assignment_id && ad >= start && ad <= end;
      });
      atts.forEach(a => {
        const s = (a.attendance_status || '').toLowerCase();
        if (s.includes('present') || s.includes('half')) presentDays += 1;
        else if (s.includes('leave')) paidLeaves += 1;
        else absentDays += 1;
        otHours += (Number(a.ot_hours) || 0);
      });
    }
    
    // Bank Details
    const ppms = (ppmReq.data?.data || []).filter(p => p.assignment_id === payslip.assignment_id);
    const ppm = ppms.length > 0 ? ppms[0] : null;
    const bank = ppm ? bankList.find(b => b.bank_id === ppm.bank_id) : null;

    // Element Entries
    const elementMap = Object.fromEntries((elReq.data?.data || []).map(e => [e.element_id, e]));
    const entries = (eeReq.data?.data || []).filter(e => e.assignment_id === payslip.assignment_id);

    // Calculate Earnings & Deductions
    const earnings = [];
    const deductions = [];
    
    entries.forEach(entry => {
       const element = elementMap[entry.element_id] || {};
       let cls = element.type || 'Unknown';
       // Map legacy 'earning' / 'deduction' to standard classes
       if (cls.toLowerCase() === 'earning' || cls.toLowerCase().includes('earning') || cls.toLowerCase().includes('allowance')) cls = 'Earning';
       if (cls.toLowerCase() === 'deduction' || cls.toLowerCase().includes('deduction') || cls.toLowerCase().includes('tax')) cls = 'Deduction';
       
       const amt = Number(entry.pay_value) || 0;
       if (amt > 0) {
         const item = { name: element.element_name || entry.element_id, amount: amt };
         if (cls === 'Earning') earnings.push(item);
         else if (cls === 'Deduction') deductions.push(item);
       }
    });

    // Handle dynamically injected components from run_id overrides (LOP, OT, Bonus, Ad-Hoc)
    // Actually, in the backend processing engine, they get saved as standard element entries or we just extract from Payslip
    // Since we don't have the explicit LOP/OT saved as entries if they were dynamic overrides not persisted in ELEMENT_ENTRY,
    // we should use the Payslip totals, and maybe parse the run_id overrides if available.
    // Assuming the Processing Pay Run uses `varInputs` and `lopByEmp` during approval, it creates element entries for them!
    // Yes, the processing engine creates `OT_AMOUNT`, `BONUS`, `AD_HOC_EARN`, `AD_HOC_DED`, `LOP_DEDUCTION` entries.
    // Let's verify if they are in the entries list. If not, we might need to manually add them based on the difference.

    const grossPay = Number(payslip.gross_earnings) || 0;
    const totalDed = Number(payslip.total_deductions) || 0;
    const netPay = Number(payslip.net_payable) || 0;

    // Fallback: If earnings sum doesn't match grossPay due to missing entries, add a catch-all
    const sumE = earnings.reduce((s, e) => s + e.amount, 0);
    const sumD = deductions.reduce((s, d) => s + d.amount, 0);
    
    if (sumE < grossPay - 1) {
       earnings.push({ name: 'Other Earnings / Arrears', amount: grossPay - sumE });
    }
    if (sumD < totalDed - 1) {
       deductions.push({ name: 'Other Deductions / LOP', amount: totalDed - sumD });
    }

    // Number to Words Converter
    const numberToWords = (num) => {
      if (num === 0) return 'Zero';
      const a = ['','One ','Two ','Three ','Four ', 'Five ','Six ','Seven ','Eight ','Nine ','Ten ','Eleven ','Twelve ','Thirteen ','Fourteen ','Fifteen ','Sixteen ','Seventeen ','Eighteen ','Nineteen '];
      const b = ['', '', 'Twenty','Thirty','Forty','Fifty', 'Sixty','Seventy','Eighty','Ninety'];
      const n = ('000000000' + num).substr(-9).match(/^(\d{2})(\d{2})(\d{2})(\d{1})(\d{2})$/);
      if (!n) return '';
      let str = '';
      str += (n[1] != 0) ? (a[Number(n[1])] || b[n[1][0]] + ' ' + a[n[1][1]]) + 'Crore ' : '';
      str += (n[2] != 0) ? (a[Number(n[2])] || b[n[2][0]] + ' ' + a[n[2][1]]) + 'Lakh ' : '';
      str += (n[3] != 0) ? (a[Number(n[3])] || b[n[3][0]] + ' ' + a[n[3][1]]) + 'Thousand ' : '';
      str += (n[4] != 0) ? (a[Number(n[4])] || b[n[4][0]] + ' ' + a[n[4][1]]) + 'Hundred ' : '';
      str += (n[5] != 0) ? ((str != '') ? 'and ' : '') + (a[Number(n[5])] || b[n[5][0]] + ' ' + a[n[5][1]]) : '';
      return str.trim() + ' Only';
    };

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const fmtINR = (n) => 'Rs. ' + n.toLocaleString('en-IN', { maximumFractionDigits: 0 });

    // Header
    doc.setFontSize(22);
    doc.setTextColor(30, 58, 138); // Blue
    doc.setFont('helvetica', 'bold');
    doc.text(company?.company_name || bg?.bg_name || 'Your Company Pvt Ltd', 15, 20);
    
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.setFont('helvetica', 'normal');
    doc.text('PAYSLIP', pageWidth - 15, 20, { align: 'right' });
    doc.setTextColor(59, 130, 246);
    doc.text(period ? period.period_name : payslip.period_name || '', pageWidth - 15, 25, { align: 'right' });
    doc.setTextColor(150);
    doc.text(`Generated on: ${new Date().toLocaleDateString('en-GB')}`, pageWidth - 15, 30, { align: 'right' });

    doc.setDrawColor(200);
    doc.line(15, 35, pageWidth - 15, 35);

    // Two-column Details Grids
    // Left: Employee Details
    autoTable(doc, {
      startY: 40,
      margin: { left: 15, right: pageWidth / 2 + 5 },
      theme: 'plain',
      head: [['EMPLOYEE DETAILS', '']],
      body: [
        ['Name:', person ? `${person.first_name || ''} ${person.last_name || ''}`.trim() : assignment?.employee_code || ''],
        ['Emp ID:', assignment?.assignment_id || ''],
        ['Department:', dept?.name || ''],
        ['Designation:', desig?.name || '']
      ],
      headStyles: { textColor: [150, 150, 150], fontSize: 9, fontStyle: 'bold' },
      bodyStyles: { textColor: [50, 50, 50], fontSize: 10 },
      columnStyles: { 0: { cellWidth: 35, fontStyle: 'normal', textColor: [100, 100, 100] }, 1: { fontStyle: 'bold' } }
    });

    // Right: Payment Details
    autoTable(doc, {
      startY: 40,
      margin: { left: pageWidth / 2 + 5, right: 15 },
      theme: 'plain',
      head: [['PAYMENT & ATTENDANCE', '']],
      body: [
        ['Bank Name:', bank?.bank_name || ppm?.bank_id || ''],
        ['Payment Method:', ppm?.payment_type || payment?.payment_method || 'Bank Transfer'],
        ['Working Days:', payslip.working_days?.toString() || ''],
        ['Paid/Present/Absent:', `${paidLeaves} / ${presentDays} / ${absentDays}`]
      ],
      headStyles: { textColor: [150, 150, 150], fontSize: 9, fontStyle: 'bold' },
      bodyStyles: { textColor: [50, 50, 50], fontSize: 10 },
      columnStyles: { 0: { cellWidth: 40, fontStyle: 'normal', textColor: [100, 100, 100] }, 1: { fontStyle: 'bold' } }
    });

    let currentY = Math.max(doc.lastAutoTable.finalY, 80) + 10;

    // Earnings & Deductions Tables (Side by side)
    const maxRows = Math.max(earnings.length, deductions.length);
    const combinedBody = [];
    for (let i = 0; i < maxRows; i++) {
       const e = earnings[i] || { name: '', amount: null };
       const d = deductions[i] || { name: '', amount: null };
       combinedBody.push([
         e.name, e.amount !== null ? fmtINR(e.amount) : '',
         d.name, d.amount !== null ? fmtINR(d.amount) : ''
       ]);
    }

    autoTable(doc, {
      startY: currentY,
      margin: { left: 15, right: 15 },
      theme: 'grid',
      head: [['EARNINGS', 'Amount', 'DEDUCTIONS', 'Amount']],
      body: combinedBody,
      headStyles: { fillColor: [241, 245, 249], textColor: [51, 65, 85], fontSize: 10, fontStyle: 'bold' },
      bodyStyles: { textColor: [50, 50, 50], fontSize: 10, cellPadding: 4 },
      columnStyles: {
        0: { cellWidth: 'auto' },
        1: { cellWidth: 35, halign: 'right', fontStyle: 'bold' },
        2: { cellWidth: 'auto' },
        3: { cellWidth: 35, halign: 'right', fontStyle: 'bold' }
      },
      styles: { lineColor: [226, 232, 240] }
    });

    currentY = doc.lastAutoTable.finalY;

    // Totals Row
    autoTable(doc, {
      startY: currentY,
      margin: { left: 15, right: 15 },
      theme: 'grid',
      body: [
        ['Total Earnings', fmtINR(grossPay), 'Total Deductions', fmtINR(totalDed)]
      ],
      bodyStyles: { textColor: [15, 23, 42], fontSize: 10, fontStyle: 'bold', fillColor: [255, 255, 255] },
      columnStyles: {
        0: { cellWidth: 'auto' },
        1: { cellWidth: 35, halign: 'right' },
        2: { cellWidth: 'auto' },
        3: { cellWidth: 35, halign: 'right' }
      },
      styles: { lineColor: [226, 232, 240] }
    });

    currentY = doc.lastAutoTable.finalY + 15;

    // Net Pay Banner
    doc.setFillColor(37, 99, 235); // Blue background
    doc.roundedRect(15, currentY, pageWidth - 30, 25, 3, 3, 'F');
    
    doc.setTextColor(255);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('Net Take Home Pay', 20, currentY + 10);
    
    doc.setFontSize(9);
    doc.setTextColor(200, 215, 255);
    doc.text(`Amount in words: Rupees ${numberToWords(netPay)}`, 20, currentY + 18);

    doc.setFontSize(22);
    doc.setTextColor(255);
    doc.setFont('helvetica', 'bold');
    doc.text(fmtINR(netPay), pageWidth - 20, currentY + 16, { align: 'right' });

    // Footer
    const footerY = doc.internal.pageSize.height - 20;
    doc.setTextColor(150);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text('This is a computer-generated payslip and does not require a signature.', 15, footerY);
    doc.text(company?.company_name || bg?.bg_name || 'Your Company Pvt Ltd', pageWidth - 15, footerY, { align: 'right' });

    // Download PDF
    const safeName = person ? person.first_name : assignment?.assignment_id || 'UNKNOWN';
    const periodStr = period ? period.period_name.replace(/\\s/g, '_').toUpperCase() : 'PERIOD';
    doc.save(`PAYSLIP_${periodStr}_${safeName}.pdf`);

  } catch (error) {
    console.error('PDF Generation Error:', error);
    throw error;
  }
};
